<template>
	<v-container fluid class="py-0">
		<v-skeleton-loader v-if="isLoading" type="table" />

		<v-row class="d-flex justify-center">
			<v-col cols="10">
				<v-tabs v-if="!isLoading" v-model="tab" grow>
					<v-tab>{{ $t('wkt.client.expensesheets.table.tabs.pending') }}</v-tab>
					<v-tab>{{ $t('wkt.client.expensesheets.table.tabs.completed') }}</v-tab>
				</v-tabs>
			</v-col>
		</v-row>

		<!-- VISTA DESKTOP -->
		<div v-if="!isLoading && !$vuetify.breakpoint.mobile">
			<v-tabs-items v-model="tab">
				<v-tab-item eager>
					<v-row v-if="!isLoading && items.filter(i => [-1,0,1,2].includes(i.status)).length === 0" class="mt-8">
						<v-col cols="12" class="pt-1 d-flex align-center justify-center flex-column">
							<div style="border-radius: 100%; width: 100px; height: 100px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px 0 rgba(0, 0, 0, .16), 0 2px 10px 0 rgba(0, 0, 0, .12);">
								<v-icon x-large style="color: grey; font-size: 4em;">layers_clear</v-icon> 
							</div>                                           
							<span class="mt-2" style="font-size: 16px; color: grey;">{{ $t('wkt.client.expensesheets.no_data') }}</span>
						</v-col>
					</v-row>
					<ExpenseSheetsTable 
					v-if="items.filter(i => [-1,0,1,2].includes(i.status)).length > 0"
					:headers="headers" 
					:items="items.filter(i => [-1,0,1,2].includes(i.status))" 
					@refresh="getItems()"
					@show-detail="showDetail($event)" />
				</v-tab-item>
				<v-tab-item eager>
					<v-row v-if="!isLoading && items.filter(i => [3,4].includes(i.status)).length === 0" class="mt-8">
						<v-col cols="12" class="pt-1 d-flex align-center justify-center flex-column">
							<div style="border-radius: 100%; width: 100px; height: 100px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px 0 rgba(0, 0, 0, .16), 0 2px 10px 0 rgba(0, 0, 0, .12);">
								<v-icon x-large style="color: grey; font-size: 4em;">layers_clear</v-icon> 
							</div>                                            
							<span class="mt-2" style="font-size: 16px; color: grey;">{{ $t('wkt.client.expensesheets.no_data') }}</span>
						</v-col>
					</v-row>
					<ExpenseSheetsTable 
					v-if="items.filter(i => [3,4].includes(i.status)).length > 0"
					:headers="headers" 
					:items="items.filter(i => [3,4].includes(i.status))" 
					@refresh="getItems()"
					@show-detail="showDetail($event)" />
				</v-tab-item>
			</v-tabs-items>
		</div>

		<!-- VISTA MOBILE -->
		<div v-if="!isLoading && $vuetify.breakpoint.mobile" class="mt-4">
			<v-tabs-items v-model="tab" class="overflow-x-hidden overflow-y-auto">
				<v-tab-item eager>
					<v-row v-if="!isLoading && items.filter(i => [-1,0,1,2].includes(i.status)).length === 0" class="mt-8">
						<v-col cols="12" class="pt-1 d-flex align-center justify-center flex-column">
							<div style="border-radius: 100%; width: 100px; height: 100px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px 0 rgba(0, 0, 0, .16), 0 2px 10px 0 rgba(0, 0, 0, .12);">
								<v-icon x-large style="color: grey; font-size: 4em;">layers_clear</v-icon> 
							</div>                                            
							<span class="mt-2" style="font-size: 16px; color: grey;">{{ $t('wkt.client.expensesheets.no_data') }}</span>
						</v-col>
					</v-row>
					<ExpenseSheetCardList 
					v-if="items.filter(i => [-1,0,1,2].includes(i.status)).length > 0"
					:items="items.filter(i => [-1,0,1,2].includes(i.status))" 
					@show-detail="showDetail($event)" />
				</v-tab-item>
				<v-tab-item eager>
					<v-row v-if="!isLoading && items.filter(i => [3,4].includes(i.status)).length === 0" class="mt-8">
						<v-col cols="12" class="pt-1 d-flex align-center justify-center flex-column">
							<div style="border-radius: 100%; width: 100px; height: 100px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 5px 0 rgba(0, 0, 0, .16), 0 2px 10px 0 rgba(0, 0, 0, .12);">
								<v-icon x-large style="color: grey; font-size: 4em;">layers_clear</v-icon> 
							</div>                                            
							<span class="mt-2" style="font-size: 16px; color: grey;">{{ $t('wkt.client.expensesheets.no_data') }}</span>
						</v-col>
					</v-row>
					<ExpenseSheetCardList 
					v-if="items.filter(i => [3,4].includes(i.status)).length > 0"
					:items="items.filter(i => [3,4].includes(i.status))" 
					@show-detail="showDetail($event)" />
				</v-tab-item>
			</v-tabs-items>
		</div>

		<v-tooltip v-if="!isLoading && tab === 0 && showAddButton" left>
			<template v-slot:activator="{ on, attrs }">				
				<v-btn 
				dark fixed 
				:bottom="!$vuetify.breakpoint.mobile" 
				right 
				fab 
				color="#185EA9" 
				v-bind="attrs" 
				v-on="on" 
				:style="`${$vuetify.breakpoint.mobile?'bottom: 63px':''}`"
				@click="addItem()">
					<v-icon dark>add</v-icon>
				</v-btn>
			</template>
			<span>{{ $t('wkt.client.expensesheets.add_expense_sheet') }}</span>
		</v-tooltip>

    <ExpenseSheetDialog ref='dlgEdit' @show-alert="doShowAlert($event)" />
    <ExpenseSheetDetailDialog ref='dlgDetail' @show-alert="doShowAlert($event)" @refresh="getItems()" />

    <!-- INICIO BLOQUE MENSAJE ESTADO PETICIONES -->
    <v-snackbar
      v-model="showAlert"
      :timeout="alertTimeout"
      :color="alertColor">
        <span style="color: #ffffff;">{{alertText}}</span>
        <v-btn text @click="showAlert=false">
          <span style="color: #ffffff;">{{ $t('wkt.client.common.dialog-buttons.close') }}</span>
        </v-btn>
    </v-snackbar>
    <!-- FIN BLOQUE ESTADO PETICIONES -->

  </v-container>
</template>

<script>
import BaseModule from '../../common/BaseModule.js'
import ExpenseSheetsTable from './ExpenseSheetsTable'
import ExpenseSheetCardList from './ExpenseSheetCardList'
import ExpenseSheetDialog from './ExpenseSheetDialog'
import ExpenseSheetDetailDialog from './ExpenseSheetDetailDialog'
import api from '../../../api/worktime'
import gwt from '../../../api/gwt'

export default {
	name: 'ExpenseSheetsModule',
	mixins: [BaseModule],
	components: {
		ExpenseSheetsTable, ExpenseSheetCardList, ExpenseSheetDialog, ExpenseSheetDetailDialog
	},
	data () {
		return { 
			tab: 0,
			company: null,
			isLoading: false,
			showAlert: false,
			alertColor: 'error',
			alertText: '',
			alertTimeout: 3000,
			options: { itemsPerPage: 20 },
			headers: [
				{ text: this.$t("wkt.client.expensesheets.table.headers.creation_date"), value: 'creationDate', width: '15%' },
				{ text: this.$t("wkt.client.expensesheets.table.headers.name"), value: 'name', width: '30%' },
				{ text: this.$t("wkt.client.expensesheets.table.headers.amount"), value: 'amount', width: '10%' },
				{ text: this.$t("wkt.client.expensesheets.table.headers.project"), value: 'projectName', width: '30%' },
				//{ text: this.$t("wkt.client.expensesheets.table.headers.approver"), value: 'approver_id', width: '25%' },
				{ text: this.$t("wkt.client.expensesheets.table.headers.status"), value: 'status', width: '10%' },
				{ text: '', value: 'actions', sortable: false, width: '120px' },
			],
			items: [],
			showAddButton: true,
		}
    },
	methods: {
		doShowAlert(payload) {
			this.alertText = payload.text;
			this.alertColor = payload.color;
			this.alertTimeout = payload.timeout;
			this.showAlert = true;
		},
		addItem() {
			this.$refs.dlgEdit.open(null, (newItem) => {
				this.getItems();
				this.showDetail(newItem);
			},
			null);
		},
		/*editItem(item) {
			const me = this;

			api.getExpenseSheetById(item.id,
				data => {
					me.openDetailDialog(data);
				},
				err => {
					console.log(err);
					this.doShowAlert({ text: err.message, color: 'error', timeout: 3000 });
				});
		},*/
		showDetail(item) {
			this.$refs.dlgDetail.open(item.id, () => {
				this.getItems();
			});
		},
		getItems() {
			this.isLoading = true;

			// eslint-disable-next-line no-unused-vars
			const { sortBy, sortDesc, page, itemsPerPage } = this.options;

			api.getExpenseSheets(this.company.id, 'id', true,
			//api.getExpenseSheets(sortBy ? sortBy : '', sortDesc,
			data => {
				this.isLoading = false;
				this.items = data;

				// En empresas con licencia BUSINESS, solo permitiremos una hoja de gastos en un estado distinto de COMPLETADA
				if (this.company.licenseType === 0) {
					this.showAddButton = false;
				} else if (this.company.licenseType === 1) {
					this.showAddButton = this.items.filter(i => [-1,0,1,2,3].includes(i.status)).length === 0;
				} else if (this.company.licenseType === 2) {
					this.showAddButton = true;
				}
			}, err => {
				this.isLoading = false;
				console.log(err);
				this.doShowAlert({ text: err.message, color: 'error', timeout: 3000 });
			});
		},
		onReveal() {      
			if (!gwt.getSessionInformation()) {
				setTimeout(this.onReveal, 1000);
				return;
			}

			this.company = gwt.getSessionInformation().company;

			this.getItems();
		}
	},
	watch: {
		/*options: {
			handler () {
				this.$log.debug(this.options);
				this.getItems();
			},
			deep: true,
		},*/
	},
}
</script>

<style scoped>
.file-input .v-icon {
	color: blue !important;
}
</style>

